import { useEffect, useMemo, useRef, useState } from 'react';
import type { PushConfiguration, PushPreferences } from '@almanac/contracts';
import { useAuth } from '../auth/AuthContext';
import { useMe } from '../api/MeContext';
import {
  DEFAULT_PUSH_PREFERENCES,
  PUSH_OWNER_KEY,
  PushRequestError,
  clearDevicePush,
  needsHomeScreen,
  pushClient,
  supportsPush,
  vapidBytes,
  withPushDevice,
} from '../lib/push';

type DeviceState =
  | 'loading'
  | 'unsupported'
  | 'home_screen'
  | 'error'
  | 'unconfigured'
  | 'blocked'
  | 'off'
  | 'on';

/** Opt-in is local to this device; other devices retain their own choices. */
export function NotificationsPage() {
  const { getToken, sessionVersion } = useAuth();
  const me = useMe();
  const [state, setState] = useState<DeviceState>('loading');
  const [configuration, setConfiguration] = useState<PushConfiguration | null>(null);
  const [preferences, setPreferences] = useState<PushPreferences>(DEFAULT_PUSH_PREFERENCES);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const currentSession = useRef({ sessionVersion, sub: me?.sub });
  if (
    currentSession.current.sessionVersion !== sessionVersion ||
    currentSession.current.sub !== me?.sub
  )
    currentSession.current = { sessionVersion, sub: me?.sub };
  const mounted = useRef(true);
  const clientSession = currentSession.current;
  const client = useMemo(
    () =>
      pushClient(async () => {
        const token = await getToken();
        if (!mounted.current || currentSession.current !== clientSession)
          throw new Error('Account changed during notification request');
        return token;
      }),
    [getToken, clientSession],
  );
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let canceled = false;
    setState('loading');
    setBusy(false);
    setConfiguration(null);
    setPreferences(DEFAULT_PUSH_PREFERENCES);
    setMessage('');
    void (async () => {
      if (needsHomeScreen()) {
        if (!canceled) setState('home_screen');
        return;
      }
      if (!supportsPush()) {
        if (!canceled) setState('unsupported');
        return;
      }
      const config = await client.configuration();
      if (canceled) return;
      setConfiguration(config);
      if (!config.configured) {
        setState('unconfigured');
        return;
      }
      if (Notification.permission === 'denied') {
        setState('blocked');
        return;
      }
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none',
      });
      await navigator.serviceWorker.ready;
      if (canceled) return;
      let subscription = await registration.pushManager.getSubscription();
      if (subscription && me?.sub && localStorage.getItem(PUSH_OWNER_KEY) !== me.sub) {
        await clearDevicePush();
        subscription = null;
      }
      if (canceled) return;
      if (!subscription) {
        setState('off');
        return;
      }
      const current = await client.status(subscription.endpoint);
      if (canceled) return;
      setPreferences(current.preferences ?? DEFAULT_PUSH_PREFERENCES);
      setState(current.enabled ? 'on' : 'off');
    })().catch(() => {
      if (!canceled) {
        setState('error');
        setMessage('Could not load notification settings. Reload to retry.');
      }
    });
    return () => {
      canceled = true;
    };
  }, [client, me?.sub, sessionVersion]);

  async function saveDevice(
    subscription: PushSubscription,
    next: PushPreferences,
    existingOnly = false,
  ) {
    const keys = subscription.toJSON().keys;
    if (!keys?.p256dh || !keys.auth) throw new Error('This browser did not supply push keys');
    const input = {
      endpoint: subscription.endpoint,
      keys: { p256dh: keys.p256dh, auth: keys.auth },
      preferences: next,
    };
    if (existingOnly) await client.update(input);
    else await client.save(input);
  }
  async function enable() {
    if (!configuration?.publicKey || !me?.sub) return;
    const publicKey = configuration.publicKey;
    const session = currentSession.current;
    const active = () => mounted.current && currentSession.current === session;
    setBusy(true);
    setMessage('');
    try {
      // The permission call is deliberately before the first await: iOS requires a tap.
      const permission = await Notification.requestPermission();
      if (!active()) return;
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'blocked' : 'off');
        return;
      }
      await withPushDevice(async (current) => {
        if (!active() || !current()) return;
        const registration = await navigator.serviceWorker.ready;
        if (!active() || !current()) return;
        const existing = await registration.pushManager.getSubscription();
        if (!active() || !current()) return;
        const subscription =
          existing ??
          (await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: vapidBytes(publicKey),
          }));
        if (!active() || !current()) {
          await subscription.unsubscribe();
          return;
        }
        try {
          await saveDevice(subscription, preferences);
        } catch (error) {
          await subscription.unsubscribe();
          throw error;
        }
        if (!active() || !current()) {
          await subscription.unsubscribe();
          return;
        }
        localStorage.setItem(PUSH_OWNER_KEY, me.sub);
        setState('on');
        const result = await client.test(subscription.endpoint);
        if (active() && current())
          setMessage(
            result.sent
              ? 'Test notification sent. Check this device.'
              : 'Enabled, but the test was not delivered. Use Send test to retry in a minute.',
          );
      });
    } catch (error) {
      if (active())
        setMessage(error instanceof Error ? error.message : 'Could not enable notifications');
    } finally {
      if (active()) setBusy(false);
    }
  }
  async function disable() {
    setBusy(true);
    setMessage('');
    try {
      const token = await getToken();
      await clearDevicePush(token ?? undefined);
      setState('off');
    } finally {
      setBusy(false);
    }
  }
  async function changePreference(key: keyof PushPreferences, checked: boolean) {
    const next = { ...preferences, [key]: checked };
    if (state !== 'on') {
      setPreferences(next);
      return;
    }
    const session = currentSession.current;
    const active = () => mounted.current && currentSession.current === session;
    setBusy(true);
    setMessage('');
    try {
      await withPushDevice(async (current) => {
        if (!active() || !current()) return;
        const subscription = await (
          await navigator.serviceWorker.getRegistration('/')
        )?.pushManager.getSubscription();
        if (!active() || !current()) return;
        if (!subscription) {
          setState('off');
          return;
        }
        await saveDevice(subscription, next, true);
        if (active() && current()) setPreferences(next);
      });
    } catch (error) {
      if (active()) {
        if (error instanceof PushRequestError && error.status === 409) {
          setState('off');
          setMessage('This device was removed. Enable notifications again to receive alerts.');
        } else setMessage('Could not save this preference. Try again.');
      }
    } finally {
      if (active()) setBusy(false);
    }
  }
  async function removeAllDevices() {
    if (
      !window.confirm(
        'Remove notifications from all your saved devices? Every device must enable notifications again.',
      )
    )
      return;
    const session = currentSession.current;
    const active = () => mounted.current && currentSession.current === session;
    setBusy(true);
    setMessage('');
    try {
      await client.removeAll();
      if (!active()) return;
      const token = await getToken();
      if (!active()) return;
      await clearDevicePush(token ?? undefined);
      if (active()) {
        setState('off');
        setMessage(
          'All saved devices removed. Enable notifications again on each device you want to use.',
        );
      }
    } catch {
      if (active()) setMessage('Could not remove saved devices. Try again.');
    } finally {
      if (active()) setBusy(false);
    }
  }
  async function test() {
    setBusy(true);
    setMessage('');
    try {
      const subscription = await (
        await navigator.serviceWorker.getRegistration('/')
      )?.pushManager.getSubscription();
      if (!subscription) {
        setState('off');
        return;
      }
      const result = await client.test(subscription.endpoint);
      setMessage(
        result.sent
          ? 'Test notification sent.'
          : 'No test delivered. Tests are limited to once per minute; retry shortly.',
      );
    } catch {
      setMessage('Could not send the test. Try again.');
    } finally {
      setBusy(false);
    }
  }
  const reason = {
    loading: 'Loading this device…',
    error: 'Notification settings could not be loaded. Reload to retry.',
    unsupported: 'This browser does not support Web Push. Try a supported browser over HTTPS.',
    home_screen:
      'On iPhone or iPad, add Almanac to the Home Screen from the Share menu, then open it from its icon to enable notifications.',
    unconfigured: 'Notifications are not configured on this Almanac server yet.',
    blocked:
      'Notifications are blocked. Allow them in this device’s browser or system settings, then reload.',
    off: 'Notifications are off on this device.',
    on: 'Notifications are on for this device.',
  }[state];
  return (
    <main className="mx-auto max-w-2xl space-y-6 px-4 py-8 sm:px-6">
      <div>
        <h1 className="text-2xl font-semibold">Notifications</h1>
        <p className="mt-2 text-muted-foreground">
          Get an alert when Almanac has something ready. Each device has its own settings.
        </p>
      </div>
      <section className="space-y-4 rounded-xl border border-border bg-card p-5">
        <h2 className="font-semibold">This device</h2>
        <p>{reason}</p>
        {state === 'off' && configuration?.publicKey && (
          <button
            type="button"
            disabled={busy || !me?.sub}
            onClick={() => void enable()}
            className="min-h-11 rounded-md bg-primary px-4 text-primary-foreground disabled:opacity-50"
          >
            Enable notifications
          </button>
        )}
        {state === 'on' && (
          <div className="flex gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => void test()}
              className="min-h-11 rounded-md border border-border px-4 disabled:opacity-50"
            >
              Send test
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void disable()}
              className="min-h-11 rounded-md border border-border px-4 disabled:opacity-50"
            >
              Turn off
            </button>
          </div>
        )}
        {(state === 'on' || state === 'off') && (
          <fieldset disabled={busy} className="space-y-3">
            <legend className="mb-3 font-medium">Notify me about</legend>
            <label className="flex min-h-11 items-center gap-3">
              <input
                type="checkbox"
                checked={preferences.research_ready}
                onChange={(event) => void changePreference('research_ready', event.target.checked)}
              />
              Completed research
            </label>
            <label className="flex min-h-11 items-center gap-3">
              <input
                type="checkbox"
                checked={preferences.digest_ready}
                onChange={(event) => void changePreference('digest_ready', event.target.checked)}
              />
              A new daily digest
            </label>
            <p className="text-sm text-muted-foreground">
              Digest alerts are off by default to avoid repeating your Slack or scheduled-assistant
              alerts. Web Push does not add another to-do reminder channel.
            </p>
          </fieldset>
        )}
        <p className="text-sm text-muted-foreground">
          Lock-screen alerts contain no private titles or research text. Signing out turns
          notifications off on this device.
        </p>
        {configuration?.configured && (
          <div className="space-y-2 border-t border-border pt-4">
            <button
              type="button"
              disabled={busy || !me?.sub}
              onClick={() => void removeAllDevices()}
              className="min-h-11 rounded-md border border-border px-4 disabled:opacity-50"
            >
              Remove all saved devices
            </button>
            <p className="text-sm text-muted-foreground">
              Use this if old devices fill your device limit. This turns off alerts on every saved
              device for your account. Each device must enable notifications again.
            </p>
          </div>
        )}
        <output className="block text-sm">{message}</output>
      </section>
    </main>
  );
}
